import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, Card, Avatar, SectionTitle, Label11, StatusBadge, Bar, Loading, Segmented, Tag, usd, int, plural, pct, ago, shortDate } from "@/components/common";
import { useCompanyName } from "@/lib/company";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { WORK_KINDS, KIND_LABELS, KIND_VIEWS, kindOf, isDocuments } from "@/lib/workKinds";
import { ENGAGEMENT_LABELS, engagementOf, titleOf } from "@/config";
import { BLANK_TASK, TaskFields, taskMissing, missingLine, taskPayload, HireDialog, FeeDialog, TaskState, TaskFigures } from "@/components/HiringTerms";
import { useInvestors, WhatTheySee, RemoveInvestorDialog } from "@/components/Investors";
import { useSeats, SeatsLine, PlanLine, addsExtra } from "@/components/Seats";
import { useFlags } from "@/lib/flags";
import { UserPlus, UserX, Lock, BriefcaseBusiness, KeyRound, Target, Copy, ChevronDown, ChevronRight, FolderGit2, FolderLock, Check, Eye, MessageSquare } from "lucide-react";

// design_handoff_crewbench/screens/Team.dc.html: who works for the company, what each one is assigned, and the
// invites still open. The founders first; then each project, a section that folds, with the independent professionals
// who work on it (a session there, one of its objectives, or folders the founder limited them to: GET /team `work.projects`).
// Someone on two projects has a card in each, with that project's numbers.
const FIRST = (name) => (name || "").split(" ")[0];
const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// a person's card: their role and responsibilities, and for an independent professional what they're doing now, their
// sessions, commits and spend, today's spend against their daily budget, the objectives assigned to them and their
// passkeys (GET /team: `work`, metadata only)
function nowLine(s) {
  if (!s) return "No open session";
  return s.status === "active" ? `In session ${s.short_id}, active ${ago(s.last_activity_at).toLowerCase()}`
    : `Session ${s.short_id} locked ${ago(s.last_activity_at).toLowerCase()}`;
}

function TodayBudget({ w }) {
  if (w.daily_cap_usd == null) return (
    <div className="flex justify-between gap-2 text-sm"><span>Today's budget</span><span className="text-muted-foreground">{usd(w.today_usd)} · no limit set</span></div>
  );
  const p = pct(w.today_usd, w.daily_cap_usd);
  const full = w.today_usd >= w.daily_cap_usd;
  return (
    <div className="flex flex-col gap-1.5 tabular-nums">
      <div className="flex justify-between gap-2 text-sm">
        <span>Today's budget</span>
        <span className={full ? "text-gr-danger" : "text-muted-foreground"}>
          {usd(w.today_usd)} of {usd(w.daily_cap_usd)}{full && " · Budget reached"}
        </span>
      </div>
      <Bar pct={p} full={full} />
    </div>
  );
}

// `here`: the project this card is in (one of `work.projects`): its numbers, its objectives and the folders the founder
// limited them to; the open session and today's budget stay the person's own
// The kind of work picks the professional's view and what the agent and the change report are told
// (design_handoff_documents_view/screens/Team.dc.html); a change applies from their next session
function KindField({ m, onKind }) {
  const kind = kindOf(m);
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Label11 id={`kind-${m.id}`}>Kind of work</Label11>
      <Select value={kind} onValueChange={(v) => v !== kind && onKind(m, v)}>
        <SelectTrigger aria-labelledby={`kind-${m.id}`} data-testid={`member-kind-${m.email}`}>
          <span className="min-w-0 truncate"><span className="font-semibold">{KIND_LABELS[kind]}</span>
            <span className="text-muted-foreground"> · {KIND_VIEWS[kind]}</span></span>
        </SelectTrigger>
        <SelectContent>
          {WORK_KINDS.map(([k, label, view]) => (
            <SelectItem key={k} value={k} data-testid={`member-kind-option-${k}`}>
              <span className="flex flex-col"><span className="font-semibold">{label}</span><span className="text-sm text-muted-foreground">{view}</span></span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Team member (the company's own staff) or Independent professional (hired for a task): the same sessions and privacy,
// a different title (backend/engagement.py, TEAM_ROLES_PLAN.md section 5). A change is saved at once and they're told.
const TITLES = [["employee", "Team member", "Your company's own staff"], ["independent", "Independent professional", "Hired for a task"]];
function TitleField({ m, onTitle }) {
  const value = engagementOf(m);
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Label11 id={`title-${m.id}`}>Title</Label11>
      <Select value={value} onValueChange={(v) => v !== value && onTitle(m, v)}>
        <SelectTrigger aria-labelledby={`title-${m.id}`} data-testid={`member-title-${m.email}`}>
          <span className="min-w-0 truncate font-semibold">{ENGAGEMENT_LABELS[value]}</span>
        </SelectTrigger>
        <SelectContent>
          {TITLES.map(([k, label, help]) => (
            <SelectItem key={k} value={k} data-testid={`member-title-option-${k}`}>
              <span className="flex flex-col"><span className="font-semibold">{label}</span><span className="text-sm text-muted-foreground">{help}</span></span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// One task on a Team card (backend/assignment_routes.py): state, fee, budget and spend; an offer or a fee change
// waiting for the professional can be withdrawn, an agreed fee changed (with their agreement)
function TaskRow({ t, first, onWithdraw, onChangeFee }) {
  const link = "underline underline-offset-2 hover:opacity-50";
  return (
    <div className="bg-card px-3 py-2.5 flex flex-col gap-2" data-testid={`task-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        {/* the title keeps room for a few words: a long state badge (Waiting for their agreement) wraps under it */}
        <span className="flex flex-col flex-[1_1_9rem] min-w-0"><span className="font-semibold">{t.objective}</span>
          {t.project && <span className="text-sm text-muted-foreground">{t.project}</span>}</span>
        <TaskState t={t} />
      </div>
      <TaskFigures t={t} />
      {t.status === "offered" && (
        <p className="m-0 text-sm text-gr-ink-secondary">Sent {shortDate(t.offered_at)}. {first} isn't on it until they agree.{" "}
          <button type="button" className={link} onClick={() => onWithdraw(t)} data-testid={`task-withdraw-${t.id}`}>Withdraw the offer</button></p>
      )}
      {t.pending && (
        <p className="m-0 text-sm text-gr-ink-secondary [text-wrap:pretty]">Fee change to {usd(t.pending.fee_usd, 0)} sent {shortDate(t.pending.at)}: “{t.pending.reason}”.
          Waiting for their agreement; the agreed {usd(t.fee_usd, 0)} stands until then.{" "}
          <button type="button" className={link} onClick={() => onWithdraw(t)} data-testid={`task-withdraw-fee-${t.id}`}>Withdraw the change</button></p>
      )}
      {t.status === "agreed" && !t.pending && (
        <div><button type="button" className={`${link} text-sm`} onClick={() => onChangeFee(t)} data-testid={`task-fee-${t.id}`}>Change the fee</button></div>
      )}
    </div>
  );
}

function MemberCard({ m, me, onRemovePasskeys, onKind, onTitle, onFinish, onHire, onWithdraw, onChangeFee, here }) {
  const coder = m.role === "coder";
  const w = m.work;
  const pid = here?.project_id;
  const session = w?.session && (!here || w.session.project_id === pid) ? w.session : null;
  // their tasks with hiring terms in this project; objectives they were put on before hiring terms existed stay listed
  const tasks = (w?.tasks || []).filter((t) => !here || t.project_id === pid);
  const objectives = (w?.objectives || []).filter((o) => (!here || o.project_id === pid) && !tasks.some((t) => t.objective_id === o.id));
  const numbers = here || w;
  const cell = (label, value) => (
    <div className="flex-[1_1_80px] bg-card px-3 py-2 flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
  return (
    <article className="bg-card rounded-[10px] p-[18px] flex flex-col gap-3.5" data-testid={`member-row-${m.email}`}>
      <div className="flex items-start gap-3">
        <Avatar name={m.name || m.email} size={36} className="text-xs" />
        <div className="flex-1 min-w-0 flex flex-col">
          <span className="font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold [overflow-wrap:anywhere]">{m.name || m.email}</span>
          <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{m.email}</span>
        </div>
        {session && <StatusBadge status={session.status} />}
      </div>
      <div className="flex flex-col gap-1 text-sm">
        <Label11>{titleOf(m)}</Label11>
        <span className="text-gr-ink-secondary">{m.responsibilities || (coder ? "No responsibilities set" : "Sets objectives and budgets, reviews change reports.")}</span>
        {!coder && me && <span className="text-muted-foreground">You</span>}
        {w && <span className="text-muted-foreground">
          {session || !here ? nowLine(session) : here.last_active_at ? `Last here ${ago(here.last_active_at).toLowerCase()}` : "No sessions here yet"}
        </span>}
      </div>
      {coder && (
        <div className="flex flex-col gap-3.5">
          <div className="grid grid-cols-2 gap-3">
            <TitleField m={m} onTitle={onTitle} />
            <KindField m={m} onKind={onKind} />
          </div>
          {w && <>
            <div className="flex flex-col gap-1.5">
              {here && <Label11>In this project</Label11>}
              <div className="flex flex-wrap gap-px bg-gr-hairline border rounded-lg overflow-hidden">
                {cell("Sessions", int(numbers.sessions))}{cell(isDocuments(kindOf(m)) ? "Versions" : "Commits", int(numbers.commits))}{cell("Spent", usd(numbers.spent_usd))}
              </div>
            </div>
            <TodayBudget w={w} />
          </>}
          {here?.folders?.length > 0 && (
            <div className="flex flex-col gap-1 text-sm" data-testid="member-folders">
              <Label11>Sees only</Label11>
              <span className="flex items-start gap-1.5"><FolderLock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span className="font-mono text-[12px] [overflow-wrap:anywhere]">{here.folders.join(", ")}</span></span>
            </div>
          )}
          <div className="flex flex-col gap-1.5 text-sm" data-testid={`member-tasks-${m.email}`}>
            <Label11>Tasks</Label11>
            {tasks.length > 0 && (
              <div className="flex flex-col gap-px bg-gr-hairline border rounded-lg overflow-hidden">
                {tasks.map((t) => <TaskRow key={t.id} t={t} first={FIRST(m.name || m.email)} onWithdraw={onWithdraw} onChangeFee={(x) => onChangeFee(x, m)} />)}
              </div>
            )}
            {objectives.map((o) => <span key={o.id} className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 shrink-0" />{o.title}
              <span className="text-muted-foreground">· no hiring terms</span></span>)}
            {!tasks.length && !objectives.length && <span className="text-muted-foreground">{here ? "No tasks in this project" : "No tasks yet"}</span>}
          </div>
          <div className="flex items-center justify-between gap-2 pt-3 border-t text-sm">
            <span className="flex items-center gap-1.5 text-muted-foreground"><KeyRound className="w-3.5 h-3.5" />{m.passkeys ? plural(m.passkeys, "passkey") : "Password only"}</span>
            {m.passkeys > 0 && (
              <button type="button" onClick={() => onRemovePasskeys(m)} className="underline underline-offset-2 hover:opacity-50"
                data-testid={`remove-passkeys-${m.email}`}>Remove passkeys</button>
            )}
          </div>
          {(onHire || onFinish) && (
            <div className="flex flex-wrap gap-2 -mt-1.5">
              {onHire && <Button size="sm" variant="outline" onClick={() => onHire(m)} data-testid={`hire-${m.email}`}>
                <BriefcaseBusiness />{engagementOf(m) === "employee" ? "Add a task" : "Hire for another task"}</Button>}
              {/* TEAM_ROLES_PLAN.md 7.9: their sessions end, their access stops, everything they leave stays */}
              {onFinish && <Button size="sm" variant="ghost" className="text-gr-danger" onClick={() => onFinish(m)}
                data-testid={`finish-${m.email}`}><UserX />{engagementOf(m) === "employee" ? "Remove from the team" : "Finish and remove"}</Button>}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// `engagement` here is who they are: an independent professional, a team member, or an investor (FEATURE_INVESTORS:
// role "viewer", no kind of work or task; backend/investor_routes.py)
function InviteDialog({ open, onOpenChange, company, initial }) {
  const qc = useQueryClient();
  const flags = useFlags();
  const inv = useInvestors();
  const seats = useSeats();  // FEATURE_WALLET: the plan's seats and slots (backend/seats.py)
  const blank = { email: "", responsibilities: "", work_kind: "code", engagement: initial === "investor" && flags.investors ? "investor" : initial === "employee" ? "employee" : "independent" };
  const [form, setForm] = useState(blank);
  const [sent, setSent] = useState(null);
  const emailRef = useRef(null);
  const [task, setTask] = useState(BLANK_TASK);
  useEffect(() => { if (open) { setForm(blank); setTask(BLANK_TASK); setSent(null); } }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps
  const invite = useMutation({
    // the hiring terms come with the invite (backend/assignment_routes.py): what for, the fee, the task budget
    mutationFn: async () => (await api.post("/team/invite", investor ? { email: form.email.trim(), role: "viewer" }
      : { email: form.email.trim(), role: "coder", responsibilities: task.responsibilities.trim(),
          work_kind: form.work_kind, engagement: form.engagement, task: taskPayload(task, form.engagement),
          accept_extra: extra })).data,
    onSuccess: (d) => {
      setSent({ email: form.email.trim(), link: `${window.location.origin}${d.invite_path}`, emailed: d.emailed });
      qc.invalidateQueries({ queryKey: ["invites"] }); qc.invalidateQueries({ queryKey: ["seats"] });
      toast.success(d.emailed ? `Invite emailed to ${form.email.trim()}.` : "Invite created.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const investor = form.engagement === "investor";
  const extra = !investor && addsExtra(seats, form.engagement);  // the plan line says what it adds; sending says yes
  const blocked = extra && !seats.can_add;  // a cancelled plan takes no extras: keep it first
  const valid = VALID_EMAIL.test(form.email.trim());
  const missing = [...(valid ? [] : ["a valid email"]), ...(investor ? [] : taskMissing(task, form.engagement))];
  const copy = async () => {
    try { await navigator.clipboard.writeText(sent.link); toast.success("Invite link copied."); }
    catch { toast.error("Copy failed. Select the link and copy it."); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px] max-h-[calc(100vh-48px)] overflow-y-auto" onOpenAutoFocus={(e) => { e.preventDefault(); emailRef.current?.focus(); }}>
        <div className="flex flex-col gap-1">
          <DialogTitle>Invite someone</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">They get an email with a link to join {company || "your company"}.</DialogDescription>
        </div>
        {!sent ? (
          <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (!missing.length && !blocked && !invite.isPending) invite.mutate(); }}>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Who are you inviting?</span>
              <Segmented label="Who are you inviting?" className="self-start max-w-full" value={form.engagement} testid="invite-title"
                options={[["independent", "Independent professional"], ["employee", "Team member"], ...(flags.investors ? [["investor", "Investor"]] : [])]}
                onChange={(v) => setForm({ ...form, engagement: v })} />
              <span className="text-sm text-muted-foreground">
                {investor ? "View only: a dashboard of how the work is going, and Messages with you."
                  : `${form.engagement === "employee" ? "Your company's own staff." : "Someone you hire for a task."} You can change it later on their card.`}</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="inv-email">Email</Label>
              <Input id="inv-email" ref={emailRef} type="email" placeholder="name@example.com" data-testid="invite-email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            {!investor && <div className="flex flex-col gap-1.5">
              <span id="inv-kind" className="text-sm font-semibold">Kind of work</span>
              <Segmented label="Kind of work" className="self-start max-w-full" value={form.work_kind} testid="invite-kind"
                options={WORK_KINDS.map(([k, label]) => [k, label])} onChange={(v) => setForm({ ...form, work_kind: v })} />
              <span className="text-sm text-muted-foreground">Sets how their workspace looks and what the AI is told about the work.</span>
              <span className="text-sm text-muted-foreground">{KIND_LABELS[form.work_kind]} opens the {KIND_VIEWS[form.work_kind].replace(/^./, (c) => c.toLowerCase())}, and they see their kind of work.</span>
            </div>}
            {investor ? <WhatTheySee sharing={inv?.sharing} /> : <TaskFields value={task} onChange={setTask} kind={form.engagement} />}
            {extra && <PlanLine s={seats} kind={form.engagement} />}
            {form.engagement === "employee" && (
              <p className="m-0 flex items-start gap-2 text-sm text-muted-foreground" data-testid="invite-privacy">
                <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />Team members get the same private sessions: you see results and spend, never their conversations.</p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">{missingLine(missing)}</span>
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button type="submit" aria-disabled={missing.length > 0 || blocked} disabled={invite.isPending} data-testid="invite-create-btn">Send invite</Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-3">
            <span className="text-gr-ink-secondary [text-wrap:pretty]">
              {sent.emailed ? `We emailed the invite to ${sent.email}. You can also share the link yourself:` : `Share this link with ${sent.email}:`}
            </span>
            <div className="flex gap-2">
              <input readOnly value={sent.link} aria-label="Invite link" data-testid="invite-link"
                className="flex-1 min-w-0 h-8 px-2.5 rounded-md border bg-background font-mono text-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
              <Button variant="outline" onClick={copy} data-testid="invite-copy-btn"><Copy />Copy link</Button>
            </div>
            <span className="text-sm text-muted-foreground">It works once and expires in 7 days.{form.engagement === "independent"
              ? " They see the task, the fee and the task budget on the invite page and tick \u201cI agree to these hiring terms\u201d before they join."
              : investor ? " They can write to you in Messages once they join. Investors are free and don't use a seat." : ""}</span>
            <div className="flex justify-end"><Button onClick={() => onOpenChange(false)}>Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Which projects are folded, kept in this browser (a convenience: the page works without it)
const FOLDED_KEY = "cb_team_folded";
function useFolded() {
  const [folded, setFolded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FOLDED_KEY) || "[]")); } catch { return new Set(); }
  });
  const toggle = (id) => setFolded((was) => {
    const next = new Set(was);
    if (next.has(id)) next.delete(id); else next.add(id);
    try { localStorage.setItem(FOLDED_KEY, JSON.stringify([...next])); } catch { /* private window: not kept */ }
    return next;
  });
  return [folded, toggle];
}

// One project: its name toggles the section open and closed; inside, a card for each person who works on it
function ProjectGroup({ id, name, people, folded, onToggle, children, icon: Icon = FolderGit2 }) {
  const working = people.filter((p) => p.here && p.m.work?.session?.status === "active" && p.m.work.session.project_id === id).length;
  const open = !folded;
  return (
    <section className="flex flex-col gap-3" data-testid={`team-project-${id}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`team-project-${id}-people`}
        className="self-stretch flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 -mx-3 rounded-md text-left transition-colors hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
        {open ? <ChevronDown className="w-4 h-4 shrink-0" aria-hidden="true" /> : <ChevronRight className="w-4 h-4 shrink-0" aria-hidden="true" />}
        <Icon className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="font-display text-[17px] leading-[1.3] tracking-[0.02em] font-semibold">{name}</span>
        <span className="text-sm text-muted-foreground">
          {people.length ? plural(people.length, "person", "people") : "Nobody yet"}
          {working > 0 && ` · ${int(working)} working now`}
        </span>
      </button>
      {open && (
        <div id={`team-project-${id}-people`}>
          {children}
        </div>
      )}
    </section>
  );
}

const expiresIn = (iso) => {
  const days = Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
  return days <= 1 ? "Expires today" : `Expires in ${days} days`;
};

// Finish and remove (design_handoff_team_roles/screens/Team.dc.html, Team-finish): what ends, what stays, and the
// founder's password (POST /team/{id}/finish). The warning names the sessions that end now; with seats (FEATURE_WALLET),
// the seat or slot they free.
function FinishDialog({ m, company, seats, pending, onOpenChange, onConfirm }) {
  const open = m?.work?.open_sessions || 0;
  const name = m?.name || m?.email;
  // a team member leaves the team; an independent professional's work is finished (the same action)
  const what = engagementOf(m) === "employee" ? "Remove from the team" : "Finish and remove";
  const kind = engagementOf(m);
  const k = seats?.[kind];
  const freed = k && (kind === "employee"
    ? `Their seat is free again: ${k.in_use - 1} of ${k.included} team member seats.`
    : `Their slot is free again: ${k.in_use - 1} of ${k.included} independent professionals.`)
    + (k.extra > 0 ? " One extra fewer from your next renewal." : "");
  const kept = [kind === "employee" ? "You keep their change reports, spend and messages." : "You keep their change reports, spend, agreed fees and messages.",
    "They're taken off the objectives still open.", ...(freed ? [freed] : []), "Their account stays, so you can invite them again later."];
  return (
    <ConfirmDestructive open={!!m} onOpenChange={onOpenChange} action={what}
      title={engagementOf(m) === "employee" ? `Remove ${name} from the team?` : `Finish and remove ${name}?`}
      pending={pending} onConfirm={onConfirm}
      warning={open ? `${plural(open, "open session")} ${open === 1 ? "ends" : "end"} now.` : "Their access ends now."}>
      <p className="m-0 [text-wrap:pretty]">This ends {FIRST(name)}'s open sessions and stops their access to {company || "your company"}.
        Their change reports are built as usual, so nothing they saved is lost.</p>
      <ul className="m-0 p-0 list-none flex flex-col gap-1.5">
        {kept.map((k) => <li key={k} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-gr-success" />{k}</li>)}
      </ul>
    </ConfirmDestructive>
  );
}

// Investors (FEATURE_INVESTORS): view only, on the Team page after the founders; Remove asks for the password
function InvestorsSection({ inv, company }) {
  const [removing, setRemoving] = useState(null);
  const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");
  return (
    <section className="flex flex-col gap-4" aria-labelledby="team-investors-title" data-testid="team-investors">
      <SectionTitle note={<>View only: objectives, money and reliability. Never sessions or your messages with the team. <Link to="/founder/settings#investors"
        className="underline underline-offset-2 hover:opacity-50">Change what investors see</Link></>}>
        <span id="team-investors-title">Investors</span>
      </SectionTitle>
      {inv.investors.length ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 items-start">
          {inv.investors.map((i) => (
            <Card key={i.id} className="px-5 py-[18px] flex flex-col gap-3.5" data-testid="investor-card">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={i.name} size={36} />
                <span className="flex-1 min-w-0 flex flex-col">
                  <span className="font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold truncate">{i.name}</span>
                  <span className="text-sm text-muted-foreground truncate">{i.email}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.09em] text-muted-foreground"><Eye className="w-3.5 h-3.5" aria-hidden="true" />View only</span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
                <span className="text-sm text-muted-foreground">{[i.joined_at && `Joined ${day(i.joined_at)}`, `Weekly summary ${i.weekly_email ? "on" : "off"}`].filter(Boolean).join(" · ")}</span>
                <Button variant="danger" onClick={() => setRemoving(i)} data-testid={`investor-remove-${i.id}`}>Remove</Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-5 text-muted-foreground [text-wrap:pretty]">
          <span className="inline-flex items-start gap-2"><MessageSquare className="w-3.5 h-3.5 mt-1 shrink-0" aria-hidden="true" />No investors yet. Invite one with “Invite someone”; they see a read-only dashboard and can write to you.</span>
        </Card>
      )}
      <RemoveInvestorDialog investor={removing} company={company} onClose={() => setRemoving(null)} />
    </section>
  );
}

export default function Team() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // ?invite=investor (Settings -> Investors' "Invite an investor") opens the dialog with Investor picked; ?invite=employee
  // or ?invite=independent (Store -> Seats and limits) with that title
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(!!params.get("invite"));
  const inv = useInvestors();
  const seatStatus = useSeats();
  const onInviteOpen = (v) => { setOpen(v); if (!v && params.get("invite")) setParams({}, { replace: true }); };
  const [target, setTarget] = useState(null);
  const [folded, toggleFolded] = useFolded();
  const { data, isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data, refetchInterval: 15000 });
  const { data: invites } = useQuery({ queryKey: ["invites"], queryFn: async () => (await api.get("/team/invites")).data });
  const company = useCompanyName();
  const remove = useMutation({
    mutationFn: async (password) => (await api.post(`/team/${target.id}/passkeys/remove`, { password })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success(`${target.name}'s passkeys removed.`); setTarget(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [finishing, setFinishing] = useState(null);
  const [hiring, setHiring] = useState(null);
  const [hireInitial, setHireInitial] = useState(null);
  // ?hire=<person>&objective=&project=&budget= (the assistant's Fill in the hiring form): Hire for another task, filled in
  useEffect(() => {
    const m = params.get("hire") && data?.members?.find((x) => x.id === params.get("hire"));
    if (!m) return;
    setHireInitial({ project_id: params.get("project") || "", objective_id: params.get("objective") || "", budget: params.get("budget") || "" });
    setHiring(m);
    setParams({}, { replace: true });
  }, [data, params]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [feeFor, setFeeFor] = useState(null);  // { task, person }
  const withdraw = useMutation({
    mutationFn: async (t) => (await api.post(`/assignments/${t.id}/withdraw`)).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success(d.withdrawn === "task" ? "Offer withdrawn. We told them." : "Fee change withdrawn. The agreed fee stays."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const finish = useMutation({
    mutationFn: async (password) => (await api.post(`/team/${finishing.id}/finish`, { password })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team"] }); qc.invalidateQueries({ queryKey: ["seats"] });  // their seat or slot is free
      toast.success(engagementOf(finishing) === "employee"
        ? `${finishing.name || finishing.email} was removed. Their reports, spend and messages are kept.`
        : `${finishing.name || finishing.email} was removed. Their reports, fees and messages are kept.`);
      setFinishing(null);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const kind = useMutation({
    mutationFn: async ({ m, k }) => api.patch(`/team/${m.id}`, { work_kind: k }),
    onSuccess: (_, { m, k }) => { qc.invalidateQueries({ queryKey: ["team"] }); toast.success(`${m.name || m.email}: ${KIND_LABELS[k]}, from their next session. We told them.`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const onKind = (m, k) => kind.mutate({ m, k });
  const title = useMutation({
    mutationFn: async ({ m, e, accept }) => api.patch(`/team/${m.id}`, { engagement: e, accept_extra: !!accept }),
    onSuccess: (_, { m, e }) => {
      ["team", "seats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${m.name || m.email} is now ${e === "employee" ? "a team member" : "an independent professional"}. We told them.`);
    },
    // beyond the plan's seats or slots (backend/seats.py): the message says what it adds, and the toast can add it
    onError: (err, v) => (err?.response?.status === 402
      ? toast(apiErr(err), { duration: 10000, action: { label: "Add it", onClick: () => title.mutate({ ...v, accept: true }) } })
      : toast.error(apiErr(err))),
  });
  const onTitle = (m, e) => title.mutate({ m, e });
  const action = <Button onClick={() => setOpen(true)} data-testid="invite-open-btn"><UserPlus />Invite someone</Button>;
  const eyebrow = [company, "Team"].filter(Boolean).join(" · ");
  const dialogs = <>
    <InviteDialog open={open} onOpenChange={onInviteOpen} company={company} initial={params.get("invite")} />
    <ConfirmDestructive open={!!target} onOpenChange={(v) => !v && setTarget(null)} title={`Remove ${target?.name}'s passkeys?`}
      action={`Remove ${plural(target?.passkeys || 0, "passkey")}`} pending={remove.isPending} onConfirm={(password) => remove.mutate(password)}>
      <p className="m-0 [text-wrap:pretty]">For a lost or replaced device. {FIRST(target?.name)} then unlocks sessions with their password until they add a new
        passkey. If they protect their sessions with their passkey, those open again only once they set up a new passkey
        with their recovery code. They get an email about it, and the audit log records it.</p>
    </ConfirmDestructive>
    <FinishDialog m={finishing} company={company} seats={seatStatus} pending={finish.isPending} onOpenChange={(v) => !v && setFinishing(null)}
      onConfirm={(password) => finish.mutate(password)} />
    <HireDialog m={hiring} initial={hireInitial} onOpenChange={(v) => { if (!v) { setHiring(null); setHireInitial(null); } }} />
    <FeeDialog task={feeFor?.task} person={feeFor?.person} onOpenChange={(v) => !v && setFeeFor(null)} />
  </>;
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow} title="Team" actions={action} /><Loading />{dialogs}</Page>;

  // the founders first (you before the others), then the team members and independent professionals
  const members = [...data.members].sort((a, b) => (a.role === "coder") - (b.role === "coder") || (b.id === user?.id) - (a.id === user?.id));
  const coders = members.filter((m) => m.role === "coder");
  const pending = invites?.invites || [];
  const founders = members.filter((m) => m.role !== "coder");
  // each project with the people on it; then those not on any project yet
  const groups = (data.projects || []).map((p) => ({
    ...p, people: coders.flatMap((m) => {
      const here = (m.work?.projects || []).find((x) => x.project_id === p.id);
      return here ? [{ m, here }] : [];
    }),
  }));
  const loose = coders.filter((m) => !(m.work?.projects || []).some((x) => groups.some((g) => g.id === x.project_id)));
  const cards = (people) => people.length ? (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 items-start">
      {people.map(({ m, here }) => <MemberCard key={m.id} m={m} here={here} onRemovePasskeys={setTarget} onKind={onKind} onTitle={onTitle} onFinish={setFinishing}
        onHire={setHiring} onWithdraw={(t) => withdraw.mutate(t)} onChangeFee={(task, person) => setFeeFor({ task, person })} />)}
    </div>
  ) : <Card className="p-5 text-muted-foreground">Nobody works on this project yet. People join it with a session, an objective of it, or folders you limit them to.</Card>;
  // "5 people: 1 founder, 1 investor, 1 team member and 2 independent professionals." (titles with nobody are left out)
  const staff = coders.filter((m) => engagementOf(m) === "employee").length;
  const investors = inv?.investors?.length || 0;
  const parts = [plural(founders.length, "founder"), investors && plural(investors, "investor"), staff && plural(staff, "team member"),
    coders.length - staff && plural(coders.length - staff, "independent professional")].filter(Boolean);
  const people = `${plural(members.length + investors, "person", "people")}: ${parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}` : parts[0]}.`;
  return (
    <Page data-testid="owner-team">
      <PageHeader eyebrow={eyebrow}
        title={`People at ${company || "your company"}.`}
        status={people}
        actions={action} />
      {seatStatus && <SeatsLine s={seatStatus} />}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 items-start" data-testid="team-founders">
        {founders.map((m) => <MemberCard key={m.id} m={m} me={m.id === user?.id} onRemovePasskeys={setTarget} />)}
      </div>
      {inv && <InvestorsSection inv={inv} company={company} />}
      <section className="flex flex-col gap-4" aria-labelledby="team-projects-title">
        <SectionTitle note="Who works on each project. Pick a project's name to fold it.">
          <span id="team-projects-title">Projects</span>
        </SectionTitle>
        {groups.map((g) => (
          <ProjectGroup key={g.id} id={g.id} name={g.name} people={g.people} folded={folded.has(g.id)} onToggle={() => toggleFolded(g.id)}>
            {cards(g.people)}
          </ProjectGroup>
        ))}
        {loose.length > 0 && (
          <ProjectGroup id="none" name="Not on a project yet" people={loose.map((m) => ({ m }))} folded={folded.has("none")}
            onToggle={() => toggleFolded("none")} icon={UserPlus}>
            {cards(loose.map((m) => ({ m })))}
          </ProjectGroup>
        )}
        {!groups.length && !loose.length && <Card className="p-5 text-muted-foreground">No projects yet. Add one on the Projects page.</Card>}
      </section>
      <section className="flex flex-col gap-3">
        <SectionTitle note="Each link works once and expires after 7 days.">Invites waiting</SectionTitle>
        {pending.length ? (
          <div className="flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden">
            {pending.map((i) => (
              <div key={i.id} className="bg-card flex flex-wrap items-center justify-between gap-4 px-4 py-3">
                <div className="flex flex-col min-w-0">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1"><span className="font-semibold [overflow-wrap:anywhere]">{i.email}</span>
                    <Tag tone="muted" className="border-gr-hairline">{titleOf(i)}</Tag>
                    {i.role === "coder" && <Tag tone="muted" className="border-gr-hairline">{KIND_LABELS[kindOf(i)]}</Tag>}</span>
                  <span className="text-sm text-muted-foreground">
                    {i.task ? [i.task.objective, i.task.fee_usd != null && `${usd(i.task.fee_usd, 0)} fixed fee`, `${usd(i.task.budget_usd, 0)} task budget`].filter(Boolean).join(" · ")
                      : (i.responsibilities || "No responsibilities set")} · Sent {ago(i.created_at).toLowerCase()}</span>
                </div>
                <Label11>{expiresIn(i.expires_at)}</Label11>
              </div>
            ))}
          </div>
        ) : <Card className="p-5 text-muted-foreground">No invites waiting.</Card>}
      </section>
      {dialogs}
    </Page>
  );
}
